import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Lead } from '../types/Lead';
import * as leadsApi from '../api/leads';

export const useUpdateLead = () => {
  const queryClient = useQueryClient();

  return useMutation<Lead, Error, { id: string; data: Partial<Lead> }>({
    mutationFn: ({ id, data }) => leadsApi.updateLead(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });
};

export const useLeads = () => {
  const { data, isLoading, error, refetch } = useQuery<Lead[]>({
    queryKey: ['leads'],
    queryFn: leadsApi.getLeads,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors
      if (error?.response?.status === 404) return false;
      // Retry other errors up to 3 times
      return failureCount < 3;
    }
  });

  return {
    leads: data || [],
    isLoading,
    error,
    refetch
  };
};

export const useCreateLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: leadsApi.createLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });
};

export const useMarkLeadAsDead = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Lead,
    Error,
    { leadId: string; dropReason: string }
  >({
    mutationFn: leadsApi.markLeadAsDead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    }
  });
};

export const useConvertToClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, clientData }: { leadId: string; clientData: any }) =>
      leadsApi.convertLeadToClient(leadId, clientData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    }
  });
};
